import { Label, PolarGrid, PolarRadiusAxis, RadialBar, RadialBarChart } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";
import { ChevronRight } from "lucide-react";

interface OpenIssueAgeData {
  averageAge: number;
  ageDistribution: { label: string; count: number; percentage: number; color: string }[];
}

interface OpenIssuesTrendCardProps {
  data: OpenIssueAgeData;
  onClick: () => void;
}

const chartConfig = {
  age: { label: "Average days open" },
  open: { label: "Open issues", color: "#f97316" },
} satisfies ChartConfig;

const DOT_COLORS = ["bg-emerald-500", "bg-amber-400", "bg-red-500"];

export function OpenIssuesTrendCard({ data, onClick }: OpenIssuesTrendCardProps) {
  const total = data.ageDistribution.reduce((sum, item) => sum + item.count, 0);
  const chartData = [{ status: "open", age: data.averageAge, fill: "var(--color-open)" }];

  return (
    <Card
      className="flex h-full cursor-pointer flex-col rounded-[15px] shadow-none transition-colors hover:bg-muted/30"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onClick(); }}
    >
      <CardHeader className="items-center pb-0 text-center">
        <CardTitle className="text-base font-medium">Open Issue Age</CardTitle>
        <p className="text-xs text-muted-foreground">Age distribution of unresolved issues</p>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col pb-4">
        <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[215px] w-full">
          <RadialBarChart data={chartData} startAngle={0} endAngle={250} outerRadius={82} innerRadius={69}>
            <PolarGrid gridType="circle" radialLines={false} stroke="none" className="first:fill-muted last:fill-background" polarRadius={[82, 69]} />
            <RadialBar dataKey="age" background cornerRadius={10} />
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                        <tspan x={viewBox.cx} y={viewBox.cy} className="fill-foreground text-4xl font-semibold">{data.averageAge.toFixed(1)}</tspan>
                        <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 23} className="fill-muted-foreground text-xs">Average days open</tspan>
                      </text>
                    );
                  }
                  return null;
                }}
              />
            </PolarRadiusAxis>
          </RadialBarChart>
        </ChartContainer>

        <div className="mt-auto space-y-2">
          {data.ageDistribution.map((item, index) => (
            <div key={item.label} className="flex items-center text-xs">
              <span className={`mr-2 size-2 rounded-full ${DOT_COLORS[index] ?? "bg-[#00B0E4]"}`} />
              <span className="text-muted-foreground">{item.label}</span>
              <span className="ml-auto font-medium tabular-nums">{item.count}</span>
              <span className="ml-2 w-9 text-right text-muted-foreground tabular-nums">{total ? Math.round((item.count / total) * 100) : 0}%</span>
            </div>
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-4 w-fit rounded-[10px] bg-white"
          onClick={(event) => { event.stopPropagation(); onClick(); }}
        >
          View open issues <ChevronRight className="ml-1 h-4 w-4 text-muted-foreground" />
        </Button>
      </CardContent>
    </Card>
  );
}
